import api from '@/api/assistant'
import readFailure from './read-failure'
import { assistantActionTypes, assistantMutationTypes } from './types'
import { ASSISTANT_CONFIRM_TYPES, ASSISTANT_FAILURES } from '@/consts/assistant'
import { ordersV2ActionTypes } from '@/store/modules/orders_v2/types'
import { actionTypes as actionTypesClients } from '@/store/modules/clients'

/**
 * Reload the lists that show wigs, the way the manual form does after a save.
 *
 * Only when the card actually differs: once a conversation owns one the backend
 * returns it on every later turn, so reloading each time would be a paginated
 * request per sentence spoken.
 */
const refreshWigLists = (dispatch, rootState, previous, current) => {
    if (!current) return
    if (JSON.stringify(previous) === JSON.stringify(current)) return

    dispatch(`ordersV2/${ordersV2ActionTypes.FETCH_ORDERS}`, null, {
        root: true,
    })

    // `refreshClient`, not `getClient`: the latter also opens the modal, which
    // with the card already open means a warning toast instead of a reload.
    const openClientId = rootState.clients?.selectedClient?.id

    if (openClientId && `${openClientId}` === `${current['client_id']}`) {
        dispatch(`clients/${actionTypesClients.refreshClient}`, openClientId, {
            root: true,
        })
    }
}

/** Send one turn and fold the answer into the store. */
const runTurn = async ({ commit, state, dispatch, rootState }, turn) => {
    commit(assistantMutationTypes.SET_SENDING, true)
    commit(assistantMutationTypes.SET_FAILURE, null)
    commit(assistantMutationTypes.SET_LAST_TURN, turn)

    // A dictated turn has nothing to show yet — the words exist only once the
    // recogniser has been through them.
    commit(assistantMutationTypes.SET_OUTGOING, {
        content: turn.text || '',
        isDictated: !!turn.audio,
    })

    const wigBefore = state.wig

    try {
        const answer = await api.sendMessage({
            ...turn,
            conversationId: state.conversationId,
            // Sent only until the conversation exists; after that it carries
            // the client itself.
            clientId: state.conversationId ? undefined : state.clientId,
        })

        commit(assistantMutationTypes.SET_TURN, answer)
        commit(assistantMutationTypes.APPEND_TURN, { sent: turn, turn: answer })
        commit(assistantMutationTypes.SET_LAST_TURN, null)

        refreshWigLists(dispatch, rootState, wigBefore, state.wig)

        return answer
    } catch (error) {
        const failure = readFailure(error)

        if (failure.type === ASSISTANT_FAILURES.NOT_FOUND) {
            // Keeping the id would make every later turn fail the same way.
            // Reset first: it clears the failure, so the message goes on after.
            commit(assistantMutationTypes.RESET)
        }

        commit(assistantMutationTypes.SET_FAILURE, failure)

        if (failure.type === ASSISTANT_FAILURES.SWITCHED_OFF) {
            commit(assistantMutationTypes.SET_SWITCHED_OFF, true)
        }

        return null
    } finally {
        commit(assistantMutationTypes.SET_SENDING, false)
        commit(assistantMutationTypes.SET_OUTGOING, null)
    }
}

export default {
    // There is no "open a conversation" call: the first turn without a
    // conversation id creates one.
    [assistantActionTypes.OPEN]: ({ commit }, { clientId } = {}) => {
        commit(assistantMutationTypes.RESET)
        commit(assistantMutationTypes.SET_CLIENT_ID, clientId)
    },

    /**
     * Drop the conversation and start another, keeping the client the panel was
     * opened with.
     *
     * The backend refuses a second client inside a bound conversation — it used
     * to quietly rebind the conversation to the new person while the card
     * stayed with the first — so starting over is the supported way through,
     * and it needs to be one action rather than closing and reopening.
     */
    [assistantActionTypes.START_OVER]: ({ commit }) => {
        commit(assistantMutationTypes.RESET)
    },

    [assistantActionTypes.SEND_TURN]: (context, turn) => runTurn(context, turn),

    [assistantActionTypes.RETRY]: (context) => {
        const { state } = context

        if (!state.lastTurn) return null

        return runTurn(context, state.lastTurn)
    },

    /**
     * The manager picked a client. Never called on their behalf, not even with
     * one candidate: a misheard name attaches the wig to the wrong person.
     *
     * `text` is required and must be in the manager's own language — a turn
     * carries text or audio, and its language is what the assistant answers in.
     */
    [assistantActionTypes.SELECT_CLIENT]: (context, { clientId, text }) => {
        // Cleared on the click rather than on the answer. Otherwise the picker
        // stays live for the length of the turn and the same client can be
        // chosen twice.
        context.commit(assistantMutationTypes.CLEAR_CLIENT_CANDIDATES)

        return runTurn(context, {
            text,
            confirm: {
                type: ASSISTANT_CONFIRM_TYPES.SELECT_CLIENT,
                value: clientId,
            },
        })
    },

    /**
     * Consent for this one request — the next turn without it asks again. The
     * answer may carry an existing client: a phone already in the system returns
     * its owner instead of creating a second card.
     */
    [assistantActionTypes.CONFIRM_CREATE_CLIENT]: (context, { text }) =>
        runTurn(context, {
            text,
            confirm: {
                type: ASSISTANT_CONFIRM_TYPES.CREATE_CLIENT,
                value: true,
            },
        }),

    [assistantActionTypes.RESET]: ({ commit }) => {
        commit(assistantMutationTypes.RESET)
    },
}
